<script setup lang="ts">
import type BaseBiometric from '@/components/user/account/security/Biometric.vue';
import type BasePassword from '@/components/user/account/security/Password.vue';
import type Social from '@/components/user/account/security/SocialAccounts.vue';
import type Base2FA from '@/components/user/account/security/TwoFactorAuth.vue';

const route = useRoute();
const { t } = useTexts();
const { fetchUserProfile } = useUserData();
const { setCurrentMenuOpenByGroup } = useAccountMenuStore();
const { checkPending } = useUserSettingsPending();
const passwordRef = ref<InstanceType<typeof BasePassword> | null>(null);
const socialAccRef = ref<InstanceType<typeof Social> | null>(null);
const twoFactorRef = ref<InstanceType<typeof Base2FA> | null>(null);
const biometricRef = ref<InstanceType<typeof BaseBiometric> | null>(null);

const { isSocialAuthEnabled } = useFeatureFlagsNew();

useSeo({
  title: t('account.security.title'),
});

const currentOpenEdit = computed(() => {
  if (passwordRef.value?.isEditing) return 'password';
  if (socialAccRef.value?.isEditing) return 'social';
  if (twoFactorRef.value?.isEditing) return 'twoFactorAuth';

  return '';
});

function checkCurrentOpenEdit(val: string) {
  if (!currentOpenEdit.value) {
    return;
  }

  return val !== currentOpenEdit.value;
}

const refsForPendencyList = computed(() => {
  const list = [passwordRef.value, twoFactorRef.value, biometricRef.value];

  if (isSocialAuthEnabled.value) {
    // @ts-ignore
    list.push(socialAccRef.value);
  }

  return list;
});

watch(
  () => refsForPendencyList.value,
  (newValues) => {
    if (!route.query.highlight) {
      return;
    }

    if (newValues.every((ref) => ref !== null)) {
      checkPending(refsForPendencyList.value);
    }
  },
  { immediate: true },
);

onMounted(() => {
  setCurrentMenuOpenByGroup('user-account');
  fetchUserProfile();
});
</script>

<template>
  <UserAccountPageLayout
    on-back="user-account"
    :title="$t('account.security.title')"
  >
    <UserBox>
      <div class="flex flex-col gap-4 md:gap-5">
        <UserAccountSecurityPassword
          ref="passwordRef"
          :opacity="checkCurrentOpenEdit('password')"
          @check-pendency="checkPending(refsForPendencyList)"
        />

        <div
          :class="[$style.separator, { [$style.highlight]: !!currentOpenEdit }]"
        />
        <UserAccountSecurityTwoFactorAuth
          ref="twoFactorRef"
          :opacity="checkCurrentOpenEdit('twoFactorAuth')"
          @check-pendency="checkPending(refsForPendencyList)"
        />

        <div
          :class="[$style.separator, { [$style.highlight]: !!currentOpenEdit }]"
        />
        <UserAccountSecurityBiometric
          ref="biometricRef"
          :opacity="checkCurrentOpenEdit('biometric')"
          @check-pendency="checkPending(refsForPendencyList)"
        />

        <template v-if="isSocialAuthEnabled">
          <div
            :class="[
              $style.separator,
              { [$style.highlight]: !!currentOpenEdit },
            ]"
          />
          <UserAccountSecuritySocialAccounts
            ref="socialAccRef"
            :opacity="checkCurrentOpenEdit('social')"
            @check-pendency="checkPending(refsForPendencyList)"
          />
        </template>

        <div
          :class="[$style.separator, { [$style.highlight]: !!currentOpenEdit }]"
        />
      </div>
    </UserBox>
  </UserAccountPageLayout>
</template>

<style module lang="scss">
.separator {
  @apply border-b border-solid border-texts/10;

  &.highlight {
    @apply opacity-60;
  }
}
</style>
